import {
  InboxOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Alert, Button, Modal, Result, Spin, Steps, Table, Upload } from 'antd';
import Papa from 'papaparse';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadCSVAction } from 'src/store/actions/appointment.action';
import { showToastSuccess } from 'src/utilities/toast';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
// import './EncountersCSVUploader.scss';
import {
  getSRColumnMappings,
  syncServiceRequests,
} from 'src/modules/SuperAdmin/store/actions/service_request';

const loadXLSX = () => import('xlsx');

const { Step } = Steps;
const { Dragger } = Upload;

const schema = {
  columns: [
    { name: 'Created Date Time', key: 'created_date_time', data_type: 'date' },
    { name: 'Start Date Time', key: 'start_date_time', data_type: 'date' },
    { name: 'Start Date', key: 'start_date', data_type: 'date' },
    { name: 'Start Time', key: 'start_time', data_type: 'time' },
    { name: 'Provider', key: 'provider', data_type: 'string' },
    { name: 'Patient', key: 'patient', data_type: 'string' },
    {
      name: 'Patient First Name',
      key: 'patient_first_name',
      data_type: 'string',
    },
    {
      name: 'Patient Last Name',
      key: 'patient_last_name',
      data_type: 'string',
    },
    { name: 'Phone Number', key: 'phone_number', data_type: 'string' },
    { name: 'Email', key: 'email', data_type: 'string' },
    {
      name: 'Service Request Type',
      key: 'service_request_type',
      data_type: 'string',
    },
    { name: 'Status', key: 'status', data_type: 'string' },
    { name: 'Birth Date', key: 'birth_date', data_type: 'date' },
    { name: 'Gender', key: 'gender', data_type: 'string' },
    { name: 'Patient EHR/MRN ID', key: 'patient_ehr_id', data_type: 'string' },
  ],
};

export const ServiceRequestsCSVUploader = ({ visible, setVisible }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [csvData, setCSVData] = useState([]);
  const [file, setFile] = useState(null);
  const [_uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, _setUploadResult] = useState(null);
  const [csvUploadedToS3, setCsvUploadedToS3] = useState(true);
  const [missingColumns, setMissingColumns] = useState([]);
  const { user } = useAuth();
  const [missingColumnsError, setMissingColumnsError] = useState(false);
  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { srColumnMappings } = useSelector(
    (state) => state?.serviceRequestState
  );

  // Fetch CSV column mappings when the modal is opened
  useEffect(() => {
    if (visible) {
      dispatch(getSRColumnMappings(currentPractitioner?.org_uuid));
    }
  }, [visible]);

  const handleClose = () => {
    setCurrentStep(0);
    setCSVData([]);
    setUploadProgress(0);
    setVisible(false);
    setMissingColumns([]);
    setMissingColumnsError(false);
  };

  const getRecords = (jsonData) => {
    let serviceRequests = [];
    let rowNumber = 2; // Start row number from 2 to avoid header row
    for (const record of jsonData) {
      serviceRequests.push({
        row_number: rowNumber,
        service_request: {
          status: record.status || '',
          created_date_time: record.created_date_time || '',
          start_date_time:
            record.start_date_time ||
            (record.start_date && record.start_time
              ? `${record.start_date} ${record.start_time}`
              : record.start_date || '') ||
            '',
          type: record.service_request_type || '',
        },
        provider: {
          name: record.provider ? String(record.provider).trim() : '',
        },
        patient: {
          birth_date: record.birth_date || '',
          name: record.patient
            ? String(record.patient).trim()
            : `${record.patient_first_name ? String(record.patient_first_name).trim() : ''} ${
                record.patient_last_name
                  ? String(record.patient_last_name).trim()
                  : ''
              }`.trim() || '',
          gender: record.gender || '',
          email: record.email ? String(record.email).trim() : '',
          phone: record.phone_number ? String(record.phone_number).trim() : '',
          ehr_id: record.patient_ehr_id
            ? String(record.patient_ehr_id).trim()
            : '',
        },
      });
      rowNumber++;
    }
    return serviceRequests;
  };

  const handleCSVUpload = async (file) => {
    try {
      let schemaCopy = JSON.parse(JSON.stringify(schema));

      if (Object.keys(srColumnMappings).length != 0) {
        for (const column of schemaCopy.columns) {
          if (
            srColumnMappings[column.name] &&
            srColumnMappings[column.name] !== ''
          ) {
            column.name = srColumnMappings[column.name];
          }
        }
      }

      const fileType = file.type === 'text/csv' ? 'CSV' : 'Excel';

      analytics.track(
        `Selected A ${fileType} File For Bulk Upload Service Requests`,
        {
          email: user?.email,
        }
      );
      setFile(file); // Store the file for later use

      let jsonData = [];
      if (file.type === 'text/csv') {
        const csvContent = await file.text(); // Read the file content as text

        // Parse CSV to JSON
        const parsedData = Papa.parse(csvContent, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: 'greedy',
          transformHeader: (header) => header.trim(),
        });

        // Map parsed data to schema dynamically
        jsonData = parsedData.data.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            rowJson[column.key] = row[column.name];
          });
          return rowJson;
        });
      } else if (
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel'
      ) {
        const XLSX = await loadXLSX();
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const parsedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
        });

        const headers = parsedData[0].map((header) =>
          String(header || '').trim()
        );

        const convertedData = parsedData
          .slice(1)
          .filter((row) => row.length > 0);

        // Use convertedData instead of parsedData in the rest of your code
        jsonData = convertedData.map((row) => {
          const rowJson = {};
          schemaCopy.columns.forEach((column) => {
            const headerIndex = headers.findIndex(
              (header) => header === column.name
            );
            if (headerIndex !== -1) {
              rowJson[column.key] = row[headerIndex] ?? null;
            }
          });
          return rowJson;
        });
      }

      const requiredColumns = [
        'created_date_time',
        'provider',
        'phone_number',
        'email',
        'service_request_type',
        'status',
        'birth_date',
      ];

      const firstRow = jsonData[0];

      const patientColumns =
        firstRow['patient'] !== undefined
          ? ['patient']
          : ['patient_first_name', 'patient_last_name'];

      const allRequiredColumns = [...requiredColumns, ...patientColumns];

      const missingColumns = allRequiredColumns.filter(
        (key) => firstRow[key] === undefined
      );

      if (missingColumns.length > 0) {
        const missingColumnNames = missingColumns.reduce((acc, key) => {
          const specialCases = {
            patient: 'Patient or Patient First Name & Patient Last Name',
            patient_first_name:
              'Patient or Patient First Name & Patient Last Name',
            patient_last_name:
              'Patient or Patient First Name & Patient Last Name',
          };

          const columnName =
            specialCases[key] ||
            schemaCopy.columns.find((column) => column.key === key)?.name;
          return columnName && !acc.includes(columnName)
            ? [...acc, columnName]
            : acc;
        }, []);

        setMissingColumns(missingColumnNames);
        setMissingColumnsError(true);
      }

      setCSVData(jsonData);

      handleNext();
    } catch (error) {
      console.error('Error reading file:', error);
    }
    return false; // Prevent upload
  };

  const generateColumns = () => {
    if (!csvData || csvData.length === 0) {
      return [];
    }
    const firstRow = csvData[0];
    const columnsToHideIfNotPresent = [
      'start_date_time',
      'start_date',
      'start_time',
      'patient',
      'patient_first_name',
      'patient_last_name',
    ];

    return schema.columns
      .map((column) => {
        if (
          columnsToHideIfNotPresent.includes(column.key) &&
          firstRow[column.key] === undefined
        ) {
          return null;
        }
        return {
          title: column.name,
          dataIndex: column.key,
          key: column.key,
        };
      })
      .filter(Boolean); // Remove null entries
  };
  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const resetProgress = () => {
    setUploadProgress(0);
    setCurrentStep(1);
    analytics.track(
      'Failed To Calculate Time Needed To Upload CSV File For Bulk Upload Service Requests',
      { email: user?.email }
    );
  };

  const uploadToS3 = async (file, key) => {
    showToastSuccess('Uploading file...');

    const base64File = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
      reader.onerror = reject;
      reader.readAsDataURL(file); // [<mediatype>][;base64],<data>
    });

    const params = {
      s3_key: key,
      body: base64File,
      content_type: file.type,
    };

    await dispatch(uploadCSVAction(params));
    setCsvUploadedToS3(true);
  };

  const handleSubmit = async () => {
    analytics.track('Submitted CSV File For Bulk Upload Service Requests', {
      email: user?.email,
    });
    setCsvUploadedToS3(false);
    const serviceRequests = getRecords(csvData);

    const date = new Date();
    const s3_key =
      `${date.getUTCFullYear()}/${('0' + (date.getUTCMonth() + 1)).slice(
        -2
      )}/${('0' + date.getUTCDate()).slice(-2)}/` +
      `${date.getTime()}` +
      `.${file.name.split('.').pop()}`;

    const syncServiceRequestsPayload = {
      s3_key: s3_key,
      service_requests: serviceRequests,
    };

    // Make the actual upload call.
    dispatch(syncServiceRequests(syncServiceRequestsPayload)).then(
      async (result) => {
        if (result) {
          analytics.track(
            'Successfully Uploaded CSV File For Bulk Upload Service Requests',
            { email: user?.email }
          );
          await uploadToS3(file, s3_key);
          setCurrentStep(2);
        } else {
          resetProgress();
          setCsvUploadedToS3(true);
        }
      }
    );
  };

  const steps = [
    {
      title: 'Upload file',
      content: (
        <>
          <div className="flex-container items-center justify-center">
            <div className="w-3/4 items-center justify-center pt-12">
              <Dragger
                accept=".csv,.xlsx,.xls"
                beforeUpload={handleCSVUpload}
                height={250}
              >
                <p className="ant-upload-drag-icon text-xs">
                  <InboxOutlined />
                </p>
                <p className="text-xs ant-upload-text">
                  Click or drag CSV, XLSX, or XLS file to this area to upload
                </p>
              </Dragger>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Preview changes',
      content: (
        <>
          {missingColumnsError ? (
            <div className="flex justify-center items-center h-64 mt-12">
              <Alert
                message="Uploaded file is missing the following required column(s)"
                description={
                  <>
                    <div>
                      <ul
                        style={{ listStyleType: 'disc', paddingLeft: '20px' }}
                      >
                        {missingColumns.map((column, index) => (
                          <li key={index}>{column}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 font-medium">
                      <p>
                        Please contact{' '}
                        <a href="mailto:help@insighthealth.ai">
                          help@insighthealth.ai
                        </a>{' '}
                        for support.
                      </p>
                    </div>
                  </>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <>
              <div className="text-xs mb-3 ml-1">
                Total Rows: {csvData.length}
              </div>
              <Table
                dataSource={csvData}
                columns={generateColumns()}
                pagination={false}
              />
            </>
          )}
        </>
      ),
    },
    {
      title: 'Result',
      content: (
        <>
          <Result
            status="success"
            title="Upload Complete"
            subTitle="An email summary will be sent to you shortly."
          />
          {uploadResult?.errors && (
            <div className="flex flex-col items-center">
              <Table
                dataSource={uploadResult.errors.map((error, index) => ({
                  key: index,
                  reason: error,
                }))}
                pagination={false}
              >
                <Table.Column
                  title="Reason for skipping"
                  dataIndex="reason"
                  key="reason"
                  className="text-xs"
                />
              </Table>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="85vw"
      className="csv-uploader text-xs"
    >
      <Steps current={currentStep}>
        {steps.map((step) => (
          <Step key={step.title} title={step.title} />
        ))}
      </Steps>
      <>
        <div className="steps-action flex justify-right mb-2">
          {currentStep === 1 &&
            !missingColumnsError &&
            (csvUploadedToS3 ? (
              <Button
                type="primary"
                onClick={handleSubmit}
                icon={<SaveOutlined />}
              >
                Submit
              </Button>
            ) : (
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            ))}
        </div>
        <div className="steps-content">{steps[currentStep].content}</div>
      </>
    </Modal>
  );
};

export default ServiceRequestsCSVUploader;
